import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@tsp-core/store/utils";

import { CompanyUuid } from "@tsp-domains/companies/types";
import { CountryUuid } from "@tsp-domains/countries/types";

import { PartyType } from "../enums";
import { PartiesDictionary, PartyDetailsRecord, PartyUuid } from "../types";

type GetPartiesResponseData = {
  items: Array<{
    uuid: PartyUuid;
    name: string;
    entityType: PartyType;
    isActive: boolean;
  }>;
};

type GetPartyDetailsRequestParams = {
  uuid: PartyUuid;
};

type GetPartyDetailsResponseData = {
  uuid: PartyUuid;
  entityType: PartyType;
  name: string;
  active: boolean;
  verified: boolean;
  relatedCompanyUuid: CompanyUuid | null;
  countryUuid: CountryUuid | null;
};

export const partiesApi = createApi({
  reducerPath: "@parties/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 60 * 60 * 24,
  tagTypes: ["PartyDetailsRecord"],
  endpoints: (builder) => ({
    getPartiesDictionary: builder.query<PartiesDictionary, void>({
      query: () => ({
        url: "/partyList",
        method: "POST",
      }),
      transformResponse: (response: GetPartiesResponseData) => {
        return response.items.reduce((accumulator, item) => {
          accumulator[item.uuid] = {
            uuid: item.uuid,
            type: item.entityType,
            name: item.name,
            isActive: item.isActive,
          };
          return accumulator;
        }, {} as PartiesDictionary);
      },
    }),
    getPartyDetails: builder.query<PartyDetailsRecord, PartyUuid>({
      query: (uuid) => {
        const body: GetPartyDetailsRequestParams = {
          uuid,
        };

        return {
          url: "/party/getParty",
          method: "POST",
          body,
        };
      },
      transformResponse: (response: GetPartyDetailsResponseData) => {
        return {
          uuid: response.uuid,
          type: response.entityType,
          name: response.name,
          isActive: response.active,
          isVerified: response.verified,
          companyUuid: response.relatedCompanyUuid,
          countryUuid: response.countryUuid,
        };
      },
      providesTags: ["PartyDetailsRecord"],
    }),
  }),
});
